/**
 * Tooltip propio del design system (reemplazo del Tooltip de MUI).
 *
 * - Renderiza la burbuja via createPortal en document.body
 * - Posicionamiento fixed con flip automatico segun viewport
 * - Visible en hover y en focus, cierre con Escape
 * - z-index: usa el token z-toast para quedar sobre modales y drawers
 */
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

type TooltipProps = {
  title: string;
  placement?: TooltipPlacement;
  /** Retardo de apertura en ms (por defecto 150). */
  delay?: number;
  disabled?: boolean;
  className?: string;
  /** Unico hijo: el elemento que dispara el tooltip. */
  children: ReactElement;
};

const GAP = 8;

type Coords = { top: number; left: number; placement: TooltipPlacement };

function computeCoords(
  target: DOMRect,
  bubble: DOMRect,
  placement: TooltipPlacement,
): Coords {
  const placements: TooltipPlacement[] =
    placement === 'top' || placement === 'bottom'
      ? [placement, placement === 'top' ? 'bottom' : 'top']
      : [placement, placement === 'left' ? 'right' : 'left'];
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  for (const p of placements) {
    let top = 0;
    let left = 0;
    if (p === 'top') {
      top = target.top - bubble.height - GAP;
      left = target.left + target.width / 2 - bubble.width / 2;
    } else if (p === 'bottom') {
      top = target.bottom + GAP;
      left = target.left + target.width / 2 - bubble.width / 2;
    } else if (p === 'left') {
      top = target.top + target.height / 2 - bubble.height / 2;
      left = target.left - bubble.width - GAP;
    } else {
      top = target.top + target.height / 2 - bubble.height / 2;
      left = target.right + GAP;
    }
    left = Math.max(GAP, Math.min(left, vw - bubble.width - GAP));
    const fits =
      p === 'top'
        ? top >= GAP
        : p === 'bottom'
          ? top + bubble.height <= vh - GAP
          : top >= GAP && top + bubble.height <= vh - GAP;
    if (fits) return { top: Math.max(GAP, top), left, placement: p };
  }
  const fallback = placements[0];
  return {
    top: Math.max(GAP, target.bottom + GAP),
    left: Math.max(GAP, target.left),
    placement: fallback,
  };
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') ref(node);
      else (ref as { current: T | null }).current = node;
    }
  };
}

export function Tooltip({
  title,
  placement = 'top',
  delay = 150,
  disabled = false,
  className,
  children,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const tooltipId = useId();
  const targetRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const close = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(false);
    setCoords(null);
  }, []);

  const scheduleOpen = () => {
    if (disabled || !title) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => setOpen(true), delay);
  };

  useEffect(() => clearTimer, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // La burbuja es fixed: ante scroll/resize se cierra para no desanclarse.
    const closeOnScroll = () => close();
    window.addEventListener('keydown', handler);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnScroll);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', closeOnScroll);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open || !targetRef.current || !bubbleRef.current) return;
    setCoords(
      computeCoords(
        targetRef.current.getBoundingClientRect(),
        bubbleRef.current.getBoundingClientRect(),
        placement,
      ),
    );
  }, [open, placement, title]);

  if (disabled || !title) return children;

  const child = children as ReactElement<{
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
    'aria-describedby'?: string;
    ref?: React.Ref<HTMLElement>;
  }>;

  // En React 18 el ref vive en `element.ref`, no en `element.props.ref`. Leerlo
  // del lugar equivocado perdía el ref original del hijo al clonarlo.
  const refOriginal = (child as { ref?: React.Ref<HTMLElement> }).ref ?? child.props.ref;
  // El `aria-describedby` propio se une al de la burbuja: reemplazarlo dejaba
  // sin anunciar el texto que el hijo ya describía.
  const descritosPropios = child.props['aria-describedby'];
  const descritos = [descritosPropios, open ? tooltipId : ''].filter(Boolean).join(' ');

  const trigger = cloneElement(child, {
    ref: mergeRefs(refOriginal, targetRef),
    'aria-describedby': descritos || undefined,
    onMouseEnter: (e: React.MouseEvent) => {
      child.props.onMouseEnter?.(e);
      scheduleOpen();
    },
    onMouseLeave: (e: React.MouseEvent) => {
      child.props.onMouseLeave?.(e);
      close();
    },
    onFocus: (e: React.FocusEvent) => {
      child.props.onFocus?.(e);
      scheduleOpen();
    },
    onBlur: (e: React.FocusEvent) => {
      child.props.onBlur?.(e);
      close();
    },
  });

  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <div
            ref={bubbleRef}
            id={tooltipId}
            role="tooltip"
            style={
              coords
                ? { top: coords.top, left: coords.left, visibility: 'visible' as const }
                : { top: 0, left: 0, visibility: 'hidden' as const }
            }
            className={cn(
              'pointer-events-none fixed z-toast max-w-xs rounded-md bg-slate-900 px-2 py-1 text-center text-xs font-medium text-white shadow-lg animate-fade-in',
              className,
            )}
          >
            {title}
          </div>,
          document.body,
        )}
    </>
  );
}
